import check from "./lib/util/check";
import extend from "./lib/util/extend";
import { isDynamic, unbox, define } from "./lib/dynamic";
import { next, cancel as _cancel } from "./lib/util/raf";
import clock from "./lib/util/clock";
import createStringStore from "./lib/strings";
import initWebGL from "./lib/webgl";
import wrapExtensions from "./lib/extension";
import wrapLimits from "./lib/limits";
import wrapBuffers from "./lib/buffer";
import wrapElements from "./lib/elements";
import wrapTextures from "./lib/texture";
import wrapRenderbuffers from "./lib/renderbuffer";
import wrapFramebuffers from "./lib/framebuffer";
import wrapAttributes from "./lib/attribute";
import wrapShaders from "./lib/shader";
import wrapRead from "./lib/read";
import createCore from "./lib/core";
import createStats from "./lib/stats";
import createTimer from "./lib/timer";
const { type, raise } = check;

const GL_COLOR_BUFFER_BIT = 16384;
const GL_DEPTH_BUFFER_BIT = 256;
const GL_STENCIL_BUFFER_BIT = 1024;

const GL_ARRAY_BUFFER = 34962;

const CONTEXT_LOST_EVENT = "webglcontextlost";
const CONTEXT_RESTORED_EVENT = "webglcontextrestored";

const DYN_PROP = 1;
const DYN_CONTEXT = 2;
const DYN_STATE = 3;

function find(haystack, needle) {
  for (let i = 0; i < haystack.length; ++i) {
    if (haystack[i] === needle) {
      return i;
    }
  }
  return -1;
}
export default function wrapREGL(args) {
  const config = initWebGL(args);
  if (!config) {
    return null;
  }

  const gl = config.gl;
  const glAttributes = gl.getContextAttributes();
  let contextLost = gl.isContextLost();

  const extensionState = wrapExtensions(gl, config);
  if (!extensionState) {
    return null;
  }

  const stringStore = createStringStore();
  const stats = createStats();
  const extensions = extensionState.extensions;
  const timer = createTimer(gl, extensions);

  const START_TIME = clock();
  const WIDTH = gl.drawingBufferWidth;
  const HEIGHT = gl.drawingBufferHeight;

  const contextState = {
    tick: 0,
    time: 0,
    viewportWidth: WIDTH,
    viewportHeight: HEIGHT,
    framebufferWidth: WIDTH,
    framebufferHeight: HEIGHT,
    drawingBufferWidth: WIDTH,
    drawingBufferHeight: HEIGHT,
    pixelRatio: config.pixelRatio,
  };
  const uniformState = {};
  const drawState = {
    elements: null,
    primitive: 4, // GL_TRIANGLES
    count: -1,
    offset: 0,
    instances: -1,
  };

  const limits = wrapLimits(gl, extensions);
  const bufferState = wrapBuffers(gl, stats, config, destroyBuffer);
  const elementState = wrapElements(gl, extensions, bufferState, stats);
  const attributeState = wrapAttributes(
    gl,
    extensions,
    limits,
    stats,
    bufferState,
    elementState,
    drawState
  );
  function destroyBuffer(buffer) {
    return attributeState.destroyBuffer(buffer);
  }
  const shaderState = wrapShaders(gl, stringStore, stats, config);
  const textureState = wrapTextures(
    gl,
    extensions,
    limits,
    function () {
      core.procs.poll();
    },
    contextState,
    stats,
    config
  );
  const renderbufferState = wrapRenderbuffers(
    gl,
    extensions,
    limits,
    stats,
    config
  );
  const framebufferState = wrapFramebuffers(
    gl,
    extensions,
    limits,
    textureState,
    renderbufferState,
    stats
  );
  const core = createCore(
    gl,
    stringStore,
    extensions,
    limits,
    bufferState,
    elementState,
    textureState,
    framebufferState,
    uniformState,
    attributeState,
    shaderState,
    drawState,
    contextState,
    timer,
    config
  );
  const readPixels = wrapRead(
    gl,
    framebufferState,
    core.procs.poll,
    contextState,
    glAttributes,
    extensions,
    limits
  );

  const nextState = core.next;
  const canvas = gl.canvas;

  const rafCallbacks = [];
  const lossCallbacks = [];
  const restoreCallbacks = [];
  const destroyCallbacks = [config.onDestroy];

  let activeRAF = null;
  function handleRAF() {
    if (rafCallbacks.length === 0) {
      if (timer) {
        timer.update();
      }
      activeRAF = null;
      return;
    }

    // schedule next animation frame
    activeRAF = next(handleRAF);

    // poll for changes
    poll();

    // fire a callback for all pending rafs
    for (let i = rafCallbacks.length - 1; i >= 0; --i) {
      const cb = rafCallbacks[i];
      if (cb) {
        cb(contextState, null, 0);
      }
    }

    // flush all pending webgl calls
    gl.flush();

    // poll GPU timers *after* gl.flush so we don't delay command dispatch
    if (timer) {
      timer.update();
    }
  }

  function startRAF() {
    if (!activeRAF && rafCallbacks.length > 0) {
      activeRAF = next(handleRAF);
    }
  }

  function stopRAF() {
    if (activeRAF) {
      _cancel(handleRAF);
      activeRAF = null;
    }
  }

  function handleContextLoss(event) {
    event.preventDefault();

    // set context lost flag
    contextLost = true;

    // pause request animation frame
    stopRAF();

    // lose context
    lossCallbacks.forEach(function (cb) {
      cb();
    });
  }

  function handleContextRestored(event) {
    // clear error code
    gl.getError();

    // clear context lost flag
    contextLost = false;

    // refresh state
    extensionState.restore();
    shaderState.restore();
    bufferState.restore();
    textureState.restore();
    renderbufferState.restore();
    framebufferState.restore();
    attributeState.restore();
    if (timer) {
      timer.restore();
    }

    // refresh state
    core.procs.refresh();

    // restart RAF
    startRAF();

    // restore context
    restoreCallbacks.forEach(function (cb) {
      cb();
    });
  }

  if (canvas) {
    canvas.addEventListener(CONTEXT_LOST_EVENT, handleContextLoss, false);
    canvas.addEventListener(
      CONTEXT_RESTORED_EVENT,
      handleContextRestored,
      false
    );
  }

  function destroy() {
    rafCallbacks.length = 0;
    stopRAF();

    if (canvas) {
      canvas.removeEventListener(CONTEXT_LOST_EVENT, handleContextLoss);
      canvas.removeEventListener(CONTEXT_RESTORED_EVENT, handleContextRestored);
    }

    shaderState.clear();
    framebufferState.clear();
    renderbufferState.clear();
    attributeState.clear();
    textureState.clear();
    elementState.clear();
    bufferState.clear();

    if (timer) {
      timer.clear();
    }

    destroyCallbacks.forEach(function (cb) {
      cb();
    });
  }

  function compileProcedure(options) {
    check(!!options, "invalid args to regl({...})");
    type(options, "object", "invalid args to regl({...})");

    function flattenNestedOptions(options) {
      const result = extend({}, options);
      delete result.uniforms;
      delete result.attributes;
      delete result.context;
      delete result.vao;

      if ("stencil" in result && result.stencil.op) {
        result.stencil.opBack = result.stencil.opFront = result.stencil.op;
        delete result.stencil.op;
      }

      function merge(name) {
        if (name in result) {
          const child = result[name];
          delete result[name];
          Object.keys(child).forEach(function (prop) {
            result[name + "." + prop] = child[prop];
          });
        }
      }
      merge("blend");
      merge("depth");
      merge("cull");
      merge("stencil");
      merge("polygonOffset");
      merge("scissor");
      merge("sample");

      if ("vao" in options) {
        result.vao = options.vao;
      }

      return result;
    }

    function separateDynamic(object, useArrays) {
      const staticItems = {};
      const dynamicItems = {};
      Object.keys(object).forEach(function (option) {
        const value = object[option];
        if (isDynamic(value)) {
          dynamicItems[option] = unbox(value, option);
          return;
        } else if (useArrays && Array.isArray(value)) {
          for (let i = 0; i < value.length; ++i) {
            if (isDynamic(value[i])) {
              dynamicItems[option] = unbox(value, option);
              return;
            }
          }
        }
        staticItems[option] = value;
      });
      return {
        dynamic: dynamicItems,
        static: staticItems,
      };
    }

    // Treat context variables separate from other dynamic variables
    const context = separateDynamic(options.context || {}, true);
    const uniforms = separateDynamic(options.uniforms || {}, true);
    const attributes = separateDynamic(options.attributes || {}, false);
    const opts = separateDynamic(flattenNestedOptions(options), false);

    const stats = {
      gpuTime: 0.0,
      cpuTime: 0.0,
      count: 0,
    };

    const compiled = core.compile(opts, attributes, uniforms, context, stats);

    const draw = compiled.draw;
    const batch = compiled.batch;
    const scope = compiled.scope;

    // FIXME: we should modify code generation for batch commands so this
    // isn't necessary
    const EMPTY_ARRAY = [];
    function reserve(count) {
      while (EMPTY_ARRAY.length < count) {
        EMPTY_ARRAY.push(null);
      }
      return EMPTY_ARRAY;
    }
    const name = options.name ? `REGLCommand(${options.name})` : "REGLCommand";
    const command = {
      [name]: function (args, body) {
        let i;
        if (contextLost) {
          raise("context lost");
        }
        if (typeof args === "function") {
          return scope.call(this, null, args, 0);
        } else if (typeof body === "function") {
          if (typeof args === "number") {
            for (i = 0; i < args; ++i) {
              scope.call(this, null, body, i);
            }
          } else if (Array.isArray(args)) {
            for (i = 0; i < args.length; ++i) {
              scope.call(this, args[i], body, i);
            }
          } else {
            return scope.call(this, args, body, 0);
          }
        } else if (typeof args === "number") {
          if (args > 0) {
            return batch.call(this, reserve(args | 0), args | 0);
          }
        } else if (Array.isArray(args)) {
          if (args.length) {
            return batch.call(this, args, args.length);
          }
        } else {
          return draw.call(this, args);
        }
      },
    };

    return extend(command[name], {
      stats: stats,
      destroy: function () {
        compiled.destroy();
      },
    });
  }

  const setFBO = (framebufferState.setFBO = compileProcedure({
    framebuffer: define.call(null, DYN_PROP, "framebuffer"),
  }));

  function clearImpl(_, options) {
    let clearFlags = 0;
    core.procs.poll();

    const c = options.color;
    if (c) {
      gl.clearColor(+c[0] || 0, +c[1] || 0, +c[2] || 0, +c[3] || 0);
      clearFlags |= GL_COLOR_BUFFER_BIT;
    }
    if ("depth" in options) {
      gl.clearDepth(+options.depth);
      clearFlags |= GL_DEPTH_BUFFER_BIT;
    }
    if ("stencil" in options) {
      gl.clearStencil(options.stencil | 0);
      clearFlags |= GL_STENCIL_BUFFER_BIT;
    }

    check(!!clearFlags, "called regl.clear with no buffer specified");
    gl.clear(clearFlags);
  }

  function clear(options) {
    check(
      typeof options === "object" && options,
      "regl.clear() takes an object as input"
    );
    if ("framebuffer" in options) {
      if (
        options.framebuffer &&
        options.framebuffer_reglType === "framebufferCube"
      ) {
        for (let i = 0; i < 6; ++i) {
          setFBO(
            extend(
              {
                framebuffer: options.framebuffer.faces[i],
              },
              options
            ),
            clearImpl
          );
        }
      } else {
        setFBO(options, clearImpl);
      }
    } else {
      clearImpl(null, options);
    }
  }

  function frame(cb) {
    type(cb, "function", "regl.frame() callback must be a function");
    rafCallbacks.push(cb);

    function cancel() {
      // FIXME:  should we check something other than equals cb here?
      // what if a user calls frame twice with the same callback...
      //
      const i = find(rafCallbacks, cb);
      check(i >= 0, "cannot cancel a frame twice");
      function pendingCancel() {
        const index = find(rafCallbacks, pendingCancel);
        rafCallbacks[index] = rafCallbacks[rafCallbacks.length - 1];
        rafCallbacks.length -= 1;
        if (rafCallbacks.length <= 0) {
          stopRAF();
        }
      }
      rafCallbacks[i] = pendingCancel;
    }

    startRAF();

    return {
      cancel: cancel,
    };
  }

  // poll viewport
  function pollViewport() {
    const viewport = nextState.viewport;
    const scissorBox = nextState.scissor_box;
    viewport[0] = viewport[1] = scissorBox[0] = scissorBox[1] = 0;
    contextState.viewportWidth =
      contextState.framebufferWidth =
      contextState.drawingBufferWidth =
      viewport[2] =
      scissorBox[2] =
        gl.drawingBufferWidth;
    contextState.viewportHeight =
      contextState.framebufferHeight =
      contextState.drawingBufferHeight =
      viewport[3] =
      scissorBox[3] =
        gl.drawingBufferHeight;
  }

  function poll() {
    contextState.tick += 1;
    contextState.time = now();
    pollViewport();
    core.procs.poll();
  }

  function refresh() {
    textureState.refresh();
    pollViewport();
    core.procs.refresh();
    if (timer) {
      timer.update();
    }
  }

  function now() {
    return (clock() - START_TIME) / 1000.0;
  }

  refresh();

  function addListener(event, callback) {
    type(callback, "function", "listener callback must be a function");

    let callbacks;
    switch (event) {
      case "frame":
        return frame(callback);
      case "lost":
        callbacks = lossCallbacks;
        break;
      case "restore":
        callbacks = restoreCallbacks;
        break;
      case "destroy":
        callbacks = destroyCallbacks;
        break;
      default:
        raise("invalid event, must be one of frame,lost,restore,destroy");
    }

    callbacks.push(callback);
    return {
      cancel: function () {
        for (let i = 0; i < callbacks.length; ++i) {
          if (callbacks[i] === callback) {
            callbacks[i] = callbacks[callbacks.length - 1];
            callbacks.pop();
            return;
          }
        }
      },
    };
  }

  const regl = extend(compileProcedure, {
    // Clear current FBO
    clear: clear,

    // Short cuts for dynamic variables
    prop: define.bind(null, DYN_PROP),
    context: define.bind(null, DYN_CONTEXT),
    this: define.bind(null, DYN_STATE),

    // executes an empty draw command
    draw: compileProcedure({}),

    // Resources
    buffer: function (options) {
      return bufferState.create(options, GL_ARRAY_BUFFER, false, false);
    },
    elements: function (options) {
      return elementState.create(options, false);
    },
    texture: textureState.create2D,
    cube: textureState.createCube,
    renderbuffer: renderbufferState.create,
    framebuffer: framebufferState.create,
    framebufferCube: framebufferState.createCube,
    vao: attributeState.createVAO,

    // Expose context attributes
    attributes: glAttributes,

    // Frame rendering
    frame: frame,
    on: addListener,

    // System limits
    limits: limits,
    hasExtension: function (name) {
      return limits.extensions.indexOf(name.toLowerCase()) >= 0;
    },

    // Read pixels
    read: readPixels,

    // Destroy regl and all associated resources
    destroy: destroy,

    // Direct GL state manipulation
    _gl: gl,
    _refresh: refresh,

    poll: function () {
      poll();
      if (timer) {
        timer.update();
      }
    },

    // Current time
    now: now,

    // regl Statistics Information
    stats: stats,
  });

  config.onDone(null, regl);

  return regl;
}
